/**
 * One person's breakout picks and lunch, readable and changeable: the
 * microsite's mock-store overrides made real. Shown at once, saved through
 * /choices (ExperienceProps.saveChoice), put back if the save says no.
 * Shared by the schedule and the place sheet so both save the same way.
 */
import { useState } from 'react';
import type { ChoiceChange, ChoicePerson } from '@/lib/types';
import type { Meals, TopicView } from './views';

export interface Picks {
  /** slot -> topic slug: hers, with anything just chosen laid over. */
  pickFor: (slot: number) => string | undefined;
  lunch: string | null;
  setBreakout: (slot: number, topic: TopicView) => Promise<void>;
  setLunch: (value: string) => Promise<void>;
  /** The last refusal, in her words. */
  problem: string | null;
}

export function usePicks(
  person: ChoicePerson | null,
  topics: TopicView[],
  meals: Meals,
  saveChoice: (change: ChoiceChange) => Promise<string | null>
): Picks {
  const [breakouts, setBreakouts] = useState<Record<number, string>>({});
  const [lunchOverride, setLunchOverride] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | null>(null);

  const server: Record<number, string> = {};
  if (person)
    for (const topic of topics)
      for (const s of topic.slots)
        if (person.picks[s.groupId] === s.optionPriceId) server[s.slot] = topic.slug;
  const serverLunch =
    person && meals.formFieldId ? (person.answers[meals.formFieldId] ?? null) : null;

  return {
    pickFor: (slot) => (person ? (breakouts[slot] ?? server[slot]) : undefined),
    lunch: person ? (lunchOverride ?? serverLunch) : null,
    problem,
    async setBreakout(slot, topic) {
      const s = topic.slots.find((x) => x.slot === slot);
      if (!person || !s) return;
      setProblem(null);
      setBreakouts((b) => ({ ...b, [slot]: topic.slug }));
      const note = await saveChoice({
        kind: 'option',
        eventParticipantId: person.eventParticipantId,
        groupId: s.groupId,
        toOptionPriceId: s.optionPriceId,
      });
      setBreakouts((b) => {
        const next = { ...b };
        delete next[slot];
        return next;
      });
      if (note) setProblem(note);
    },
    async setLunch(value) {
      if (!person || !meals.formFieldId) return;
      setProblem(null);
      setLunchOverride(value);
      const note = await saveChoice({
        kind: 'answer',
        eventParticipantId: person.eventParticipantId,
        formFieldId: meals.formFieldId,
        value,
      });
      setLunchOverride(undefined);
      if (note) setProblem(note);
    },
  };
}
